import React, { useState } from 'react';
import { ExternalLinkIcon, FileTextIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import type { ChoirEvent, DocKind } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, inputClass } from '../ui/Field';
import { docKindClass, docKindLabel, formatDate } from '../../utils/labels';
import { useApp } from '../../contexts/AppContext';
const vrstniRed: DocKind[] = ['vabilo', 'dnevni_red', 'gradivo', 'priloga', 'zapisnik'];
interface EventDocumentsProps {
  event: ChoirEvent;
  /** Vodstvo lahko dodaja in odstranjuje dokumente. */
  urejanje?: boolean;
}
export function EventDocuments({
  event,
  urejanje = false
}: EventDocumentsProps) {
  const {
    addDocument,
    removeDocument,
    pushToast,
    today
  } = useApp();
  const [odprtoDodajanje, setOdprtoDodajanje] = useState(false);
  const [ime, setIme] = useState('');
  const [kind, setKind] = useState<DocKind>('gradivo');
  const dokumenti = [...(event.dokumenti ?? [])].sort((a, b) => vrstniRed.indexOf(a.kind) - vrstniRed.indexOf(b.kind));
  function dodaj() {
    if (!ime.trim()) return;
    addDocument(event.id, {
      ime: ime.trim(),
      kind,
      dodano: today,
      link: 'https://drive.google.com/drive/folders/mepz-viva-gradiva'
    });
    setIme('');
    setKind('gradivo');
    setOdprtoDodajanje(false);
    pushToast('Dokument je dodan k dogodku.');
  }
  return <Card className="p-5" data-id="element-302" data-mp="24gvhlcoywwro">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-303" data-mp="24ej173yuyugq">
        <div data-id="element-304" data-mp="28h5uzaf3apgk">
          <h2 className="text-base font-semibold text-ink" data-id="element-305" data-mp="28fup952337mt">Gradiva dogodka</h2>
          <p className="mt-0.5 text-sm text-ink-muted" data-id="element-306" data-mp="1uho50rq2565m">
            Dokumenti so shranjeni v Google Drive mapi zbora in vidni udeležencem dogodka.
          </p>
        </div>
        {urejanje && <Button variant="secondary" size="sm" onClick={() => setOdprtoDodajanje(v => !v)} data-id="element-307" data-mp="j1fykf7i9aoo">
            <PlusIcon className="h-4 w-4" data-id="element-308" data-mp="o3opz74qc4oo" />
            Dodaj dokument
          </Button>}
      </div>

      {odprtoDodajanje && <div className="mt-4 grid gap-3 rounded-lg border border-line bg-canvas/60 p-3 sm:grid-cols-[1.6fr_1fr_auto] sm:items-end" data-id="element-309" data-mp="3w3s0srqrytc0">
          <Field label="Ime dokumenta" htmlFor="docIme" data-id="element-310" data-mp="3rc1o4f4d7thf">
            <input id="docIme" value={ime} onChange={e => setIme(e.target.value)} placeholder="Npr. Zapisnik rednega zbora članov.pdf" className={inputClass} data-id="element-311" data-mp="3raqo8qe1j5vl" />
          </Field>
          <Field label="Vrsta" htmlFor="docKind" data-id="element-312" data-mp="3ce9ajsidpf5v">
            <select id="docKind" value={kind} onChange={e => setKind(e.target.value as DocKind)} className={inputClass} data-id="element-313" data-mp="38dxr2j1r5kvz">
              {vrstniRed.map(k => <option key={k} value={k} data-id="element-314" data-mp="16lt47lz41ds4">
                  {docKindLabel[k]}
                </option>)}
            </select>
          </Field>
          <Button onClick={dodaj} disabled={!ime.trim()} data-id="element-315" data-mp="197a97lpsbxw3">
            Shrani
          </Button>
        </div>}

      {dokumenti.length === 0 ? <p className="mt-4 rounded-lg bg-canvas px-4 py-6 text-center text-[13px] text-ink-muted" data-id="element-316" data-mp="1e52av7g8eydh">
          Za ta dogodek še ni gradiv.
        </p> : <ul className="mt-3 divide-y divide-line" data-id="element-317" data-mp="1nwokizcx8pe4">
          {dokumenti.map(d => <li key={d.id} className="flex items-center gap-3 py-3" data-id="element-318" data-mp="q8o8i7y6k8sp">
              <FileTextIcon className="h-4 w-4 shrink-0 text-ink-muted" data-id="element-319" data-mp="qaivdrt0iazf" />
              <a href={d.link} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm font-medium text-ink hover:text-green-700" data-id="element-320" data-mp="v8amijlu1t2k">
                {d.ime}
              </a>
              <Badge className={docKindClass[d.kind]} data-id="element-321" data-mp="2bmamfukohipt">{docKindLabel[d.kind]}</Badge>
              <span className="hidden shrink-0 text-[12px] text-ink-muted sm:block" data-id="element-322" data-mp="2fn2uikhtdhki">
                {formatDate(d.dodano)}
              </span>
              <a href={d.link} target="_blank" rel="noreferrer" aria-label={`Odpri ${d.ime}`} className="rounded-md p-1.5 text-ink-muted transition-colors duration-150 ease-out hover:bg-canvas hover:text-ink" data-id="element-323" data-mp="2e6pc8jne7otp">
                <ExternalLinkIcon className="h-4 w-4" data-id="element-324" data-mp="2j3p8y3ycrf85" />
              </a>
              {urejanje && <button onClick={() => removeDocument(event.id, d.id)} aria-label={`Odstrani ${d.ime}`} className="rounded-md p-1.5 text-ink-muted transition-colors duration-150 ease-out hover:bg-rose-50 hover:text-rose-600" data-id="element-325" data-mp="2x38eqq3oyeha">
                  <Trash2Icon className="h-4 w-4" data-id="element-326" data-mp="31wjniws2d5bu" />
                </button>}
            </li>)}
        </ul>}
    </Card>;
}